/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-serving.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * How the model is reached. All three speak the OpenAI chat-completions wire format, which is why
 * one adapter serves them and the backend selects an endpoint and a model rather than a client.
 * `llama_cpp` is GGUF on CPU inside the customer's network — the air-gapped default, and the one
 * most likely to fail `core.ai.evals`' capability bar, which is a product fact rather than a
 * defect. `hosted` is outside the customer's network and is **never** selectable for an air-gapped
 * tenant.
 */
export type ServingBackend = 'llama_cpp' | 'vllm' | 'hosted';

export const SERVING_BACKENDS: readonly ServingBackend[] = ['llama_cpp', 'vllm', 'hosted'];

export function isServingBackend(value: unknown): value is ServingBackend {
  return (['llama_cpp', 'vllm', 'hosted'] as readonly unknown[]).includes(value);
}

/**
 * Which feature is asking, and therefore which validator must run. Closed, and with **no
 * general-purpose member** — a general completion has no validator that could be written for it,
 * so it could only be gated by something that accepts anything, which is hard rule 8 satisfied on
 * paper and absent in fact. `copilot_answer` was missing here for one release while the route
 * iterated every member and returned it, so this authoritative spec was narrower than the API it
 * describes — the same defect `ai-evals.yaml` carried for `Capability`, and invisible for the same
 * reason: a generated envelope guard does not validate items. Adding a `Purpose` without adding it
 * here is now a failing test rather than a silent divergence.
 */
export type Purpose = 'pii_classification' | 'rule_suggestion' | 'anomaly_explanation' | 'copilot_answer';

export const PURPOSES: readonly Purpose[] = ['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'];

export function isPurpose(value: unknown): value is Purpose {
  return (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(value);
}

/**
 * Why a validator refused a completion. A closed vocabulary because these are **counted**:
 * `core.ai.evals`' per-backend capability matrix is built from this distribution, and a model that
 * invents columns cannot be trusted with a feature while one that is merely slow can be retried.
 * `content_filtered` and `provider_error` are deliberately separate: the model's own safety layer
 * declining is *their* behaviour, an unreachable endpoint is *ours*. `core.ai.evals` exempts our
 * outages from a capability bar and must not exempt their refusals, or declining to answer becomes
 * a way for a model to stay unproven and therefore permitted. `rate_limited` is an outage like
 * `provider_error` — the model never answered, so it is not counted against a capability bar —
 * kept separate so a client can say the provider's quota or rate limit is exhausted rather than
 * that the model could not be reached. `ungrounded` is the one that matters most — output
 * referring to something not in the input. An invented column attaches a verdict to an entity
 * nobody can open while displacing the real one, which on a PII screen means a column of Aadhaar
 * numbers reported clean.
 */
export type RejectionReason = 'truncated' | 'malformed' | 'out_of_vocabulary' | 'ungrounded' | 'out_of_range' | 'empty' | 'content_filtered' | 'provider_error' | 'rate_limited';

export const REJECTION_REASONS: readonly RejectionReason[] = ['truncated', 'malformed', 'out_of_vocabulary', 'ungrounded', 'out_of_range', 'empty', 'content_filtered', 'provider_error', 'rate_limited'];

export function isRejectionReason(value: unknown): value is RejectionReason {
  return (['truncated', 'malformed', 'out_of_vocabulary', 'ungrounded', 'out_of_range', 'empty', 'content_filtered', 'provider_error', 'rate_limited'] as readonly unknown[]).includes(value);
}

export interface PurposeEntry {
  readonly code: Purpose;
  /** The validator that must accept a completion for this purpose. */
  readonly validated_by: string;
}

export function isPurposeEntry(value: unknown): value is PurposeEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(candidate['code']) &&
    typeof candidate['validated_by'] === 'string'
  );
}

export interface ServingCapabilities {
  /** ADR-004's ladder position. Always 1 — metadata only, never a row. */
  readonly rung: number;
  readonly purposes: readonly PurposeEntry[];
  /**
   * What the AI plane will never put in a prompt. Read from the deployment rather than written
   * here, so it cannot drift from the enum under review.
   */
  readonly never_sent: readonly string[];
  readonly rejection_reasons: readonly RejectionReason[];
  readonly max_prompt_chars: number;
  readonly validator_policy: string;
}

export function isServingCapabilities(value: unknown): value is ServingCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    Array.isArray(candidate['purposes']) &&
    (candidate['purposes'] as readonly unknown[]).every((item) => isPurposeEntry(item)) &&
    Array.isArray(candidate['never_sent']) &&
    (candidate['never_sent'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['rejection_reasons']) &&
    (candidate['rejection_reasons'] as readonly unknown[]).every((item) => isRejectionReason(item)) &&
    typeof candidate['max_prompt_chars'] === 'number' &&
    typeof candidate['validator_policy'] === 'string'
  );
}

export interface ServingRouteView {
  /**
   * `false` with a `note` when no model is routed. Deliberately not an empty route object: "nobody
   * set this up" and "somebody switched it off" are different facts, and the second is
   * `configured: true, enabled: false`.
   */
  readonly configured: boolean;
  readonly note?: string;
  readonly backend?: ServingBackend;
  readonly endpoint?: string;
  readonly model?: string;
  readonly air_gapped?: boolean;
  readonly enabled?: boolean;
}

export function isServingRouteView(value: unknown): value is ServingRouteView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['configured'] === 'boolean'
  );
}

/** A provider integration, each with its own adapter. */
export type ProviderKind = 'gemini' | 'openai' | 'anthropic' | 'vertex_ai' | 'azure_openai' | 'azure_ai_foundry' | 'aws_bedrock' | 'ibm_watsonx' | 'oci_generative_ai' | 'openai_compatible' | 'vllm' | 'ollama';

export const PROVIDER_KINDS: readonly ProviderKind[] = ['gemini', 'openai', 'anthropic', 'vertex_ai', 'azure_openai', 'azure_ai_foundry', 'aws_bedrock', 'ibm_watsonx', 'oci_generative_ai', 'openai_compatible', 'vllm', 'ollama'];

export function isProviderKind(value: unknown): value is ProviderKind {
  return (['gemini', 'openai', 'anthropic', 'vertex_ai', 'azure_openai', 'azure_ai_foundry', 'aws_bedrock', 'ibm_watsonx', 'oci_generative_ai', 'openai_compatible', 'vllm', 'ollama'] as readonly unknown[]).includes(value);
}

/** The vendor's own API, a cloud's managed AI service, or a tenant-run endpoint. */
export type ProviderCategory = 'direct' | 'enterprise' | 'custom';

export const PROVIDER_CATEGORYS: readonly ProviderCategory[] = ['direct', 'enterprise', 'custom'];

export function isProviderCategory(value: unknown): value is ProviderCategory {
  return (['direct', 'enterprise', 'custom'] as readonly unknown[]).includes(value);
}

/** How a provider call authenticates. */
export type AuthMethod = 'platform_managed' | 'api_key' | 'bearer_token' | 'none' | 'entra_client_secret' | 'managed_identity' | 'aws_access_key' | 'aws_assume_role' | 'aws_web_identity' | 'gcp_service_account' | 'gcp_workload_identity' | 'ibm_api_key' | 'oci_api_key';

export const AUTH_METHODS: readonly AuthMethod[] = ['platform_managed', 'api_key', 'bearer_token', 'none', 'entra_client_secret', 'managed_identity', 'aws_access_key', 'aws_assume_role', 'aws_web_identity', 'gcp_service_account', 'gcp_workload_identity', 'ibm_api_key', 'oci_api_key'];

export function isAuthMethod(value: unknown): value is AuthMethod {
  return (['platform_managed', 'api_key', 'bearer_token', 'none', 'entra_client_secret', 'managed_identity', 'aws_access_key', 'aws_assume_role', 'aws_web_identity', 'gcp_service_account', 'gcp_workload_identity', 'ibm_api_key', 'oci_api_key'] as readonly unknown[]).includes(value);
}

/** The result of the last real connection test. `untested` is never reported as connected. */
export type ConnectionStatus = 'untested' | 'connected' | 'connection_failed' | 'invalid_credentials' | 'model_not_found' | 'deployment_not_found' | 'access_denied' | 'rate_limited' | 'timeout' | 'region_unsupported' | 'configuration_incomplete';

export const CONNECTION_STATUSES: readonly ConnectionStatus[] = ['untested', 'connected', 'connection_failed', 'invalid_credentials', 'model_not_found', 'deployment_not_found', 'access_denied', 'rate_limited', 'timeout', 'region_unsupported', 'configuration_incomplete'];

export function isConnectionStatus(value: unknown): value is ConnectionStatus {
  return (['untested', 'connected', 'connection_failed', 'invalid_credentials', 'model_not_found', 'deployment_not_found', 'access_denied', 'rate_limited', 'timeout', 'region_unsupported', 'configuration_incomplete'] as readonly unknown[]).includes(value);
}

export interface ProviderField {
  readonly name: string;
  readonly label: string;
  readonly required: boolean;
  /** True when the value is stored in Vault, never returned, and masked on entry. */
  readonly sensitive: boolean;
  readonly placeholder: string;
  readonly choices: readonly string[];
  readonly default: string;
  /** The methods this field applies to. Empty means every method. */
  readonly auth_methods: readonly AuthMethod[];
  readonly help: string;
}

export function isProviderField(value: unknown): value is ProviderField {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['name'] === 'string' &&
    typeof candidate['label'] === 'string' &&
    typeof candidate['required'] === 'boolean' &&
    typeof candidate['sensitive'] === 'boolean' &&
    typeof candidate['placeholder'] === 'string' &&
    Array.isArray(candidate['choices']) &&
    (candidate['choices'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['default'] === 'string' &&
    Array.isArray(candidate['auth_methods']) &&
    (candidate['auth_methods'] as readonly unknown[]).every((item) => isAuthMethod(item)) &&
    typeof candidate['help'] === 'string'
  );
}

export interface ProviderSpec {
  readonly kind: ProviderKind;
  readonly category: ProviderCategory;
  readonly display_name: string;
  readonly description: string;
  readonly auth_methods: readonly AuthMethod[];
  readonly fields: readonly ProviderField[];
  readonly model_label: string;
  readonly model_placeholder: string;
  /** Non-empty where a deployment name is distinct from the model (Azure). */
  readonly deployment_label: string;
  readonly is_default: boolean;
  readonly hosted: boolean;
}

export function isProviderSpec(value: unknown): value is ProviderSpec {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['gemini', 'openai', 'anthropic', 'vertex_ai', 'azure_openai', 'azure_ai_foundry', 'aws_bedrock', 'ibm_watsonx', 'oci_generative_ai', 'openai_compatible', 'vllm', 'ollama'] as readonly unknown[]).includes(candidate['kind']) &&
    (['direct', 'enterprise', 'custom'] as readonly unknown[]).includes(candidate['category']) &&
    typeof candidate['display_name'] === 'string' &&
    typeof candidate['description'] === 'string' &&
    Array.isArray(candidate['auth_methods']) &&
    (candidate['auth_methods'] as readonly unknown[]).every((item) => isAuthMethod(item)) &&
    Array.isArray(candidate['fields']) &&
    (candidate['fields'] as readonly unknown[]).every((item) => isProviderField(item)) &&
    typeof candidate['model_label'] === 'string' &&
    typeof candidate['model_placeholder'] === 'string' &&
    typeof candidate['deployment_label'] === 'string' &&
    typeof candidate['is_default'] === 'boolean' &&
    typeof candidate['hosted'] === 'boolean'
  );
}

export interface ProviderCatalog {
  readonly items: readonly ProviderSpec[];
}

export function isProviderCatalog(value: unknown): value is ProviderCatalog {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isProviderSpec(item))
  );
}

export interface CredentialStatus {
  readonly configured: boolean;
  /** The NAMES of the stored credential fields. Never a value, never a masked suffix. */
  readonly fields: readonly string[];
  readonly updated_at: string | null;
}

export function isCredentialStatus(value: unknown): value is CredentialStatus {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['configured'] === 'boolean' &&
    Array.isArray(candidate['fields']) &&
    (candidate['fields'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (typeof candidate['updated_at'] === 'string' || candidate['updated_at'] === null)
  );
}

export interface ModelConfigView {
  readonly id: string;
  readonly provider_id: string;
  readonly display_name: string;
  readonly model_identifier: string;
  readonly deployment_name: string;
  readonly parameters: Readonly<Record<string, number>>;
  readonly enabled: boolean;
  readonly status: ConnectionStatus;
  readonly status_detail: string;
  readonly last_tested_at: string | null;
  readonly latency_ms: number | null;
}

export function isModelConfigView(value: unknown): value is ModelConfigView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['provider_id'] === 'string' &&
    typeof candidate['display_name'] === 'string' &&
    typeof candidate['model_identifier'] === 'string' &&
    typeof candidate['deployment_name'] === 'string' &&
    candidate['parameters'] !== undefined &&
    typeof candidate['enabled'] === 'boolean' &&
    (['untested', 'connected', 'connection_failed', 'invalid_credentials', 'model_not_found', 'deployment_not_found', 'access_denied', 'rate_limited', 'timeout', 'region_unsupported', 'configuration_incomplete'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['status_detail'] === 'string' &&
    (typeof candidate['last_tested_at'] === 'string' || candidate['last_tested_at'] === null) &&
    (typeof candidate['latency_ms'] === 'number' || candidate['latency_ms'] === null)
  );
}

export interface ModelConfigList {
  readonly items: readonly ModelConfigView[];
}

export function isModelConfigList(value: unknown): value is ModelConfigList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isModelConfigView(item))
  );
}

export interface ProviderConfigView {
  readonly id: string;
  readonly kind: ProviderKind;
  readonly category: ProviderCategory;
  readonly display_name: string;
  readonly auth_method: AuthMethod;
  readonly settings: Readonly<Record<string, string>>;
  readonly endpoint_origin: string;
  readonly hosted: boolean;
  readonly credential: CredentialStatus;
  readonly enabled: boolean;
  readonly status: ConnectionStatus;
  readonly status_detail: string;
  readonly last_tested_at: string | null;
  readonly latency_ms: number | null;
  readonly created_by: string;
  readonly updated_by: string;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly models: readonly ModelConfigView[];
}

export function isProviderConfigView(value: unknown): value is ProviderConfigView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['gemini', 'openai', 'anthropic', 'vertex_ai', 'azure_openai', 'azure_ai_foundry', 'aws_bedrock', 'ibm_watsonx', 'oci_generative_ai', 'openai_compatible', 'vllm', 'ollama'] as readonly unknown[]).includes(candidate['kind']) &&
    (['direct', 'enterprise', 'custom'] as readonly unknown[]).includes(candidate['category']) &&
    typeof candidate['display_name'] === 'string' &&
    (['platform_managed', 'api_key', 'bearer_token', 'none', 'entra_client_secret', 'managed_identity', 'aws_access_key', 'aws_assume_role', 'aws_web_identity', 'gcp_service_account', 'gcp_workload_identity', 'ibm_api_key', 'oci_api_key'] as readonly unknown[]).includes(candidate['auth_method']) &&
    candidate['settings'] !== undefined &&
    typeof candidate['endpoint_origin'] === 'string' &&
    typeof candidate['hosted'] === 'boolean' &&
    candidate['credential'] !== undefined &&
    typeof candidate['enabled'] === 'boolean' &&
    (['untested', 'connected', 'connection_failed', 'invalid_credentials', 'model_not_found', 'deployment_not_found', 'access_denied', 'rate_limited', 'timeout', 'region_unsupported', 'configuration_incomplete'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['status_detail'] === 'string' &&
    (typeof candidate['last_tested_at'] === 'string' || candidate['last_tested_at'] === null) &&
    (typeof candidate['latency_ms'] === 'number' || candidate['latency_ms'] === null) &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['updated_by'] === 'string' &&
    (typeof candidate['created_at'] === 'string' || candidate['created_at'] === null) &&
    (typeof candidate['updated_at'] === 'string' || candidate['updated_at'] === null) &&
    Array.isArray(candidate['models']) &&
    (candidate['models'] as readonly unknown[]).every((item) => isModelConfigView(item))
  );
}

export interface ProviderConfigList {
  readonly items: readonly ProviderConfigView[];
}

export function isProviderConfigList(value: unknown): value is ProviderConfigList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isProviderConfigView(item))
  );
}

export interface ConnectionTestResult {
  readonly status: ConnectionStatus;
  readonly latency_ms: number | null;
  /** Host-written. Never the provider's error body. */
  readonly detail: string;
  readonly tested_at: string;
}

export function isConnectionTestResult(value: unknown): value is ConnectionTestResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['untested', 'connected', 'connection_failed', 'invalid_credentials', 'model_not_found', 'deployment_not_found', 'access_denied', 'rate_limited', 'timeout', 'region_unsupported', 'configuration_incomplete'] as readonly unknown[]).includes(candidate['status']) &&
    (typeof candidate['latency_ms'] === 'number' || candidate['latency_ms'] === null) &&
    typeof candidate['detail'] === 'string' &&
    typeof candidate['tested_at'] === 'string'
  );
}

export interface Deletion {
  readonly id: string;
  readonly deleted: boolean;
  readonly credential_deleted?: boolean;
}

export function isDeletion(value: unknown): value is Deletion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['deleted'] === 'boolean'
  );
}

export interface ServedModel {
  readonly model: ModelConfigView;
  readonly provider_id: string;
  readonly provider_kind: ProviderKind;
  readonly provider_name: string;
  readonly category: ProviderCategory;
  readonly auth_method: AuthMethod;
}

export function isServedModel(value: unknown): value is ServedModel {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate['model'] !== undefined &&
    typeof candidate['provider_id'] === 'string' &&
    (['gemini', 'openai', 'anthropic', 'vertex_ai', 'azure_openai', 'azure_ai_foundry', 'aws_bedrock', 'ibm_watsonx', 'oci_generative_ai', 'openai_compatible', 'vllm', 'ollama'] as readonly unknown[]).includes(candidate['provider_kind']) &&
    typeof candidate['provider_name'] === 'string' &&
    (['direct', 'enterprise', 'custom'] as readonly unknown[]).includes(candidate['category']) &&
    (['platform_managed', 'api_key', 'bearer_token', 'none', 'entra_client_secret', 'managed_identity', 'aws_access_key', 'aws_assume_role', 'aws_web_identity', 'gcp_service_account', 'gcp_workload_identity', 'ibm_api_key', 'oci_api_key'] as readonly unknown[]).includes(candidate['auth_method'])
  );
}

export interface DirectRoute {
  readonly backend: ServingBackend;
  readonly endpoint_origin: string;
  readonly model: string;
}

export function isDirectRoute(value: unknown): value is DirectRoute {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['llama_cpp', 'vllm', 'hosted'] as readonly unknown[]).includes(candidate['backend']) &&
    typeof candidate['endpoint_origin'] === 'string' &&
    typeof candidate['model'] === 'string'
  );
}

export interface RoutingView {
  readonly mode: 'configured_model' | 'direct_route' | 'unconfigured';
  readonly enabled: boolean;
  readonly air_gapped: boolean;
  readonly active: ServedModel | null;
  readonly fallback: ServedModel | null;
  readonly fallback_enabled: boolean;
  readonly direct_route: DirectRoute | null;
}

export function isRoutingView(value: unknown): value is RoutingView {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['configured_model', 'direct_route', 'unconfigured'] as readonly unknown[]).includes(candidate['mode']) &&
    typeof candidate['enabled'] === 'boolean' &&
    typeof candidate['air_gapped'] === 'boolean' &&
    (candidate['active'] === null || isServedModel(candidate['active'])) &&
    (candidate['fallback'] === null || isServedModel(candidate['fallback'])) &&
    typeof candidate['fallback_enabled'] === 'boolean' &&
    (candidate['direct_route'] === null || isDirectRoute(candidate['direct_route']))
  );
}
